import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getCurrentSupplier } from '../../../../redux/services/authService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { openDillToast } from '../../../../utils/helpers';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { Copy, MapPin, QrCode } from 'phosphor-react';
import QRCodePrintModal from './QRCodePrintModal';
import { isFormTemplateType } from '@dill/dill-shared';
import { getCreditApplicationTemplateLabel } from '../CreditApplicationTemplate/CONSTANTS';

const GetCreditApplicationLinkModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { users } = useAppSelector((state) => state.auth);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );

  const [selectedSalesRep, setSelectedSalesRep] = useState<string>('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [qrUrl, setQrUrl] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    if (open && currentSupplier?.id) {
      dispatch(getCurrentSupplier({ supplierId: currentSupplier.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const templateOptions = useMemo(() => {
    return creditApplicationTemplates
      .filter((template) => !isFormTemplateType(template.applicationType))
      .map((template) => ({
        label: getCreditApplicationTemplateLabel(template),
        value: template.id,
        isExternalForm: template.applicationType === 'EXTERNAL_FORM',
      }));
  }, [creditApplicationTemplates]);
  // Credit / COD applications only: external forms go out per sales rep or branch, never as a
  // company link, and the General Application only offers credit / COD.
  const applicationOptions = templateOptions.filter((option) => !option.isExternalForm);

  const handleCloseModal = () => {
    setSelectedSalesRep('');
    setSelectedBranch('');
    setQrUrl(null);
    handleClose();
  };

  const buildLink = (templateId: string, repId?: string, branchId?: string) => {
    let link = `${process.env.REACT_APP_FRONTEND_URL}/requestCreditApplication?domainId=${
      currentSupplier?.domainId ?? ''
    }&templateId=${templateId}`;
    if (repId) link = `${link}&salesRepId=${repId}`;
    if (branchId) link = `${link}&branchId=${branchId}`;
    return link;
  };

  const buildGeneralLink = (repId?: string, branchId?: string) => {
    let link = `${process.env.REACT_APP_FRONTEND_URL}/requestCreditApplication?domainId=${
      currentSupplier?.domainId ?? ''
    }`;
    if (repId) link = `${link}&salesRepId=${repId}`;
    if (branchId) link = `${link}&branchId=${branchId}`;
    return link;
  };

  const handleCopy = async (text: string, repId?: string, branchId?: string) => {
    try {
      const link = buildLink(text, repId, branchId);
      await navigator.clipboard.writeText(link);
      openDillToast({
        message: 'Link copied to clipboard',
        type: 'SUCCESS',
      });
    } catch (err) {
      console.error('Failed to copy:', err);
      openDillToast({
        message: 'Link failed to copy',
        type: 'ERROR',
      });
    }
  };

  const salesRepsUsers = useMemo(() => {
    return (users ?? [])
      .filter((usr) => usr.roles?.includes('CREDIT_APP_SALES'))
      .map((usr) => ({
        label: `${usr.firstName ?? ''} ${usr.lastName ?? ''}`.trim(),
        value: usr.id ?? '',
      }))
      .filter((rep) => rep.value);
  }, [users]);

  const branchOptions = useMemo(() => {
    return (supplierBranches ?? [])
      .filter((branch) => !branch.notInCreditApp)
      .map((branch) => ({
        label: branch.branchName || branch.branchId,
        value: branch.branchId,
      }))
      .filter((branch) => branch.value);
  }, [supplierBranches]);

  // The branches the selected sales rep is on (branch salesRep entries carry the rep's user id).
  const selectedSalesRepBranches = useMemo(
    () =>
      branchOptions.filter((option) =>
        (supplierBranches ?? []).some(
          (branch) =>
            branch.branchId === option.value &&
            (branch.salesRep ?? []).some((rep) => rep?.id === selectedSalesRep)
        )
      ),
    [branchOptions, supplierBranches, selectedSalesRep]
  );

  // Copy / QR rows for every template (plus the General Application when there's more than one),
  // with the sales rep and/or branch on the link.
  const renderLinkRows = (repId?: string, branchId?: string, branchLabel?: string) => (
    <>
      {applicationOptions.length > 1 && (
        <div className="flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-100">
          <span className="text-sm text-gray-700">General Application</span>
          <div className="flex items-center gap-1">
            <DillButton
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(buildGeneralLink(repId, branchId));
                  openDillToast({ message: 'Link copied to clipboard', type: 'SUCCESS' });
                } catch {
                  openDillToast({ message: 'Link failed to copy', type: 'ERROR' });
                }
              }}
              variant="ghost-blue"
              label="Copy"
              icon={<Copy size={14} weight="bold" />}
              height={28}
            />
            <DillButton
              onClick={() =>
                setQrUrl({
                  url: buildGeneralLink(repId, branchId),
                  title: [branchLabel, 'General Application Link'].filter(Boolean).join(' - '),
                })
              }
              variant="icon-blue"
              icon={<QrCode size={16} />}
              height={28}
              width={28}
            />
          </div>
        </div>
      )}
      {templateOptions.map((option, i) => (
        <div
          key={option.value}
          className={`flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 transition-colors ${
            i < templateOptions.length - 1 ? 'border-b border-gray-100' : ''
          }`}>
          <span className="text-sm text-gray-700">{option.label}</span>
          <div className="flex items-center gap-1">
            <DillButton
              onClick={() => handleCopy(option.value, repId, branchId)}
              variant="ghost-blue"
              label="Copy"
              icon={<Copy size={14} weight="bold" />}
              height={28}
            />
            <DillButton
              onClick={() =>
                setQrUrl({
                  url: buildLink(option.value, repId, branchId),
                  title: [branchLabel, option.label].filter(Boolean).join(' - '),
                })
              }
              variant="icon-blue"
              icon={<QrCode size={16} />}
              height={28}
              width={28}
            />
          </div>
        </div>
      ))}
    </>
  );

  return (
    <>
      {qrUrl && (
        <QRCodePrintModal
          open={!!qrUrl}
          onClose={() => setQrUrl(null)}
          url={qrUrl.url}
          title={qrUrl.title}
        />
      )}
      <DillModal
        open={open}
        onClose={handleCloseModal}
        title="Get Application Link"
        maxWidth="540px"
        footer={<DillButton onClick={handleCloseModal} variant="secondary" label="Close" />}>
        {templateOptions.length === 0 && (
          <div className="flex w-full justify-center items-center border py-10 rounded-lg">
            <p className="text-sm text-gray-500">No credit/COD application templates found.</p>
          </div>
        )}

        {templateOptions.length > 0 && (
          <>
            {applicationOptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Company Application Links
                </p>
                <div className="rounded-lg border border-gray-100 overflow-hidden">
                  {applicationOptions.length > 1 && (
                    <div className="flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-100">
                      <span className="text-sm text-gray-700">General Application</span>
                      <div className="flex items-center gap-1">
                        <DillButton
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(buildGeneralLink());
                              openDillToast({
                                message: 'Link copied to clipboard',
                                type: 'SUCCESS',
                              });
                            } catch {
                              openDillToast({ message: 'Link failed to copy', type: 'ERROR' });
                            }
                          }}
                          variant="ghost-blue"
                          label="Copy"
                          icon={<Copy size={14} weight="bold" />}
                          height={28}
                        />
                        <DillButton
                          onClick={() =>
                            setQrUrl({ url: buildGeneralLink(), title: 'General Application Link' })
                          }
                          variant="icon-blue"
                          icon={<QrCode size={16} />}
                          height={28}
                          width={28}
                        />
                      </div>
                    </div>
                  )}
                  {applicationOptions.map((option, i) => (
                    <div
                      key={option.value}
                      className={`flex items-center justify-between px-3 py-2.5 hover:bg-blue-50 transition-colors ${
                        i < applicationOptions.length - 1 ? 'border-b border-gray-100' : ''
                      }`}>
                      <span className="text-sm text-gray-700">{option.label}</span>
                      <div className="flex items-center gap-1">
                        <DillButton
                          onClick={() => handleCopy(option.value)}
                          variant="ghost-blue"
                          label="Copy"
                          icon={<Copy size={14} weight="bold" />}
                          height={28}
                        />
                        <DillButton
                          onClick={() =>
                            setQrUrl({ url: buildLink(option.value), title: option.label })
                          }
                          variant="icon-blue"
                          icon={<QrCode size={16} />}
                          height={28}
                          width={28}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Sales Rep Application Links
              </p>
              <div className="rounded-lg border border-gray-100 overflow-hidden">
                <div className="p-3">
                  <DillDropdown
                    label="Select Sales Rep"
                    value={selectedSalesRep}
                    items={salesRepsUsers}
                    containerClassName="h-[36px]"
                    onSelectChange={(item) => {
                      if (item?.value) setSelectedSalesRep(item.value);
                    }}
                  />
                </div>
                {selectedSalesRep && (
                  <div className="border-t border-gray-100">
                    {selectedSalesRepBranches.length === 0
                      ? renderLinkRows(selectedSalesRep)
                      : selectedSalesRepBranches.map((branch, i) => (
                          <div
                            key={branch.value}
                            className={i > 0 ? 'border-t border-gray-100' : ''}>
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                              <MapPin size={12} weight="bold" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide truncate">
                                {branch.label}
                              </span>
                            </div>
                            {renderLinkRows(selectedSalesRep, branch.value, branch.label)}
                          </div>
                        ))}
                  </div>
                )}
              </div>
            </div>

            {branchOptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Branch Application Links
                </p>
                <div className="rounded-lg border border-gray-100 overflow-hidden">
                  <div className="p-3">
                    <DillDropdown
                      label="Select Branch"
                      value={selectedBranch}
                      items={branchOptions}
                      containerClassName="h-[36px]"
                      onSelectChange={(item) => {
                        if (item?.value) setSelectedBranch(item.value);
                      }}
                    />
                  </div>
                  {selectedBranch && (
                    <div className="border-t border-gray-100">
                      {renderLinkRows(undefined, selectedBranch)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </DillModal>
    </>
  );
};

export default GetCreditApplicationLinkModal;
